import type { RunnerInfo, User } from "@studio/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, RotateCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Skills } from "@/components/SkillsAdmin";
import { Button, IconButton, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { cn, formatClock } from "@/lib/format";

const field = "h-10 w-full rounded-[var(--radius-control)] border border-line bg-panel px-3 text-[16px] outline-none focus-visible:border-mute";

/** People, the assistant's skills and GPU servers: who may sign in, what the assistant knows, which server belongs to whom. */
export function AdminPage() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  if (me.data && me.data.user.role !== "admin") {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="Admin" />
        <p className="px-6 text-mute">This page is for administrators.</p>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Admin" />
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[880px] flex-col gap-10 px-4 pb-10 pt-4 md:px-6">
          <People />
          <Skills />
          <Runners />
        </div>
      </div>
    </div>
  );
}

function Secret({ label, value, onDone }: { label: string; value: string; onDone: () => void }) {
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-film/50 bg-film/10 p-3">
      <p className="text-[13px] font-medium">
        {label} — copy it now, it is not shown again.
      </p>
      <div className="flex items-center gap-2">
        <code className="tabular min-w-0 flex-1 truncate rounded-[8px] bg-desk px-2 py-1.5 text-[13px]">{value}</code>
        <IconButton
          label="Copy"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            toast.success("Copied.");
          }}
        >
          <Copy className="size-5" />
        </IconButton>
        <Button size="sm" variant="quiet" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

function People() {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: api.users });
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [secret, setSecret] = useState<{ label: string; value: string } | null>(null);

  const refresh = () => {
    void users.refetch();
    void queryClient.invalidateQueries({ queryKey: ["stats"] });
  };
  const create = useMutation({
    mutationFn: () => api.createUser({ username: username.trim(), displayName: displayName.trim() || undefined }),
    onSuccess: (created) => {
      setUsername("");
      setDisplayName("");
      if (created.temporaryPassword) setSecret({ label: `Temporary password for ${created.user.username}`, value: created.temporaryPassword });
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.updateUser>[1] }) => api.updateUser(id, body),
    onSuccess: (updated) => {
      if (updated.temporaryPassword) setSecret({ label: `Temporary password for ${updated.user.username}`, value: updated.temporaryPassword });
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: refresh,
    onError: (error) => toast.error(error.message),
  });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[17px] font-semibold">People</h2>
      <p className="max-w-[62ch] text-[14px] text-mute">
        Everyone sees only their own chats and library. New accounts get a temporary password, shown once.
      </p>
      {secret ? <Secret label={secret.label} value={secret.value} onDone={() => setSecret(null)} /> : null}
      <div className="flex flex-col gap-2 md:flex-row">
        <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" autoCapitalize="none" className={field} />
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Name shown to others" className={field} />
        <Button variant="outline" className="h-10 shrink-0" disabled={!username.trim() || create.isPending} onClick={() => create.mutate()}>
          <Plus className="size-4" aria-hidden />
          Add person
        </Button>
      </div>
      <ul className="flex flex-col divide-y divide-line/60 rounded-[var(--radius-control)] border border-line">
        {users.data?.map((user: User) => (
          <li key={user.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px]">
                {user.displayName} <span className="text-mute">· {user.username}</span>
                {user.role === "admin" ? <span className="ml-2 rounded-full bg-raised px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide">admin</span> : null}
                {user.disabled ? <span className="ml-2 rounded-full bg-danger/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-danger">off</span> : null}
              </p>
              <p className="tabular text-[13px] text-mute">
                joined {formatClock(user.createdAt)}
                {user.lastSeenAt ? ` · last here ${formatClock(user.lastSeenAt)}` : " · never signed in"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <Button size="sm" variant="quiet" onClick={() => update.mutate({ id: user.id, body: { resetPassword: true } })}>
                Reset password
              </Button>
              <Button size="sm" variant="quiet" onClick={() => update.mutate({ id: user.id, body: { role: user.role === "admin" ? "user" : "admin" } })}>
                {user.role === "admin" ? "Make ordinary" : "Make admin"}
              </Button>
              <Button size="sm" variant="quiet" onClick={() => update.mutate({ id: user.id, body: { disabled: !user.disabled } })}>
                {user.disabled ? "Turn on" : "Turn off"}
              </Button>
              <IconButton label={`Delete ${user.username}`} onClick={() => remove.mutate(user.id)}>
                <Trash2 className="size-5" />
              </IconButton>
            </div>
          </li>
        )) ?? <li className="px-3 py-3"><Spinner /></li>}
      </ul>
    </section>
  );
}

function Runners() {
  const users = useQuery({ queryKey: ["users"], queryFn: api.users });
  const runners = useQuery({ queryKey: ["admin-runners"], queryFn: api.adminRunners, refetchInterval: 10_000 });
  const [name, setName] = useState("");
  const [secret, setSecret] = useState<{ label: string; value: string } | null>(null);

  const create = useMutation({
    mutationFn: () => api.createRunner({ name: name.trim() }),
    onSuccess: (made) => {
      setName("");
      if (made.token) setSecret({ label: `Token for ${made.runner.name}`, value: made.token });
      void runners.refetch();
    },
    onError: (error) => toast.error(error.message),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.updateRunner>[1] }) => api.updateRunner(id, body),
    onSuccess: (updated) => {
      if (updated.token) setSecret({ label: `New token for ${updated.runner.name}`, value: updated.token });
      void runners.refetch();
    },
    onError: (error) => toast.error(error.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteRunner(id),
    onSuccess: () => runners.refetch(),
    onError: (error) => toast.error(error.message),
  });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[17px] font-semibold">GPU servers</h2>
      <p className="max-w-[62ch] text-[14px] text-mute">
        Each server runs the studio's runner and takes jobs it can do. Reserve one and only that person's work goes there.
      </p>
      {secret ? <Secret label={secret.label} value={secret.value} onDone={() => setSecret(null)} /> : null}
      <div className="flex flex-col gap-2 md:flex-row">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. 5090 box" className={field} />
        <Button variant="outline" className="h-10 shrink-0" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
          <Plus className="size-4" aria-hidden />
          Add server
        </Button>
      </div>
      <div className="flex flex-col gap-3">
        {runners.data?.map((runner: RunnerInfo) => (
          <article key={runner.id} className="rounded-[var(--radius-control)] border border-line bg-panel p-3">
            <header className="flex flex-wrap items-center gap-2">
              <span className={cn("size-2 rounded-full", runner.online ? "bg-ok" : "bg-mute")} aria-hidden />
              <p className="text-[15px] font-medium">{runner.name}</p>
              <p className="tabular text-[13px] text-mute">
                {runner.online ? "online" : runner.lastSeenAt ? `last seen ${formatClock(runner.lastSeenAt)}` : "never connected"}
                {runner.hostname ? ` · ${runner.hostname}` : ""}
                {runner.version ? ` · v${runner.version}` : ""}
              </p>
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="quiet" onClick={() => update.mutate({ id: runner.id, body: { rotateToken: true } })}>
                  <RotateCw className="size-4" aria-hidden />
                  New token
                </Button>
                <Button size="sm" variant="quiet" onClick={() => update.mutate({ id: runner.id, body: { disabled: !runner.disabled } })}>
                  {runner.disabled ? "Turn on" : "Turn off"}
                </Button>
                <IconButton label={`Remove ${runner.name}`} onClick={() => remove.mutate(runner.id)}>
                  <Trash2 className="size-5" />
                </IconButton>
              </div>
            </header>

            <ul className="mt-2 flex flex-col gap-1">
              {runner.slots.map((slot) => (
                <li key={slot.id} className="tabular text-[13px] text-mute">
                  {slot.name ?? slot.id}: {slot.state}
                  {slot.loadedModel ? ` · ${slot.loadedModel} loaded` : ""} · {slot.models.map((m) => m.id).join(", ")}
                  {slot.gpus.length ? ` · GPU ${slot.gpus.join(", ")}` : ""}
                  {slot.llm?.busy
                    ? ` · outside LLM busy (${slot.llm.inflight} running${slot.llm.waiting ? `, ${slot.llm.waiting} waiting` : ""})`
                    : ""}
                </li>
              ))}
              {runner.unavailable.map((item) => (
                <li key={item.model} className="text-[13px] text-mute">
                  {item.model}: {item.reason}
                </li>
              ))}
            </ul>

            <fieldset className="mt-3 border-t border-line/60 pt-2">
              <legend className="sr-only">Who may use {runner.name}</legend>
              <p className="text-[13px] font-medium text-mute">Reserved for</p>
              <div className="mt-1.5 flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-[14px]">
                  <input
                    type="checkbox"
                    checked={!runner.reservedFor || runner.reservedFor.length === 0}
                    onChange={() => update.mutate({ id: runner.id, body: { reservedFor: null } })}
                    className="size-4 accent-[var(--color-film)]"
                  />
                  Everyone
                </label>
                {users.data?.map((user) => {
                  const checked = runner.reservedFor?.some((r) => r.id === user.id) ?? false;
                  return (
                    <label key={user.id} className="flex items-center gap-2 text-[14px]">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const current = runner.reservedFor?.map((r) => r.id) ?? [];
                          const next = checked ? current.filter((id) => id !== user.id) : [...current, user.id];
                          update.mutate({ id: runner.id, body: { reservedFor: next.length ? next : null } });
                        }}
                        className="size-4 accent-[var(--color-film)]"
                      />
                      {user.displayName}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </article>
        )) ?? <Spinner />}
        {runners.data?.length === 0 ? (
          <p className="text-[14px] text-mute">No GPU servers yet. Add one, then put its token in the runner's config.</p>
        ) : null}
      </div>
    </section>
  );
}
